import { Box, Heading, Tooltip, Icon, HStack, Text } from "@chakra-ui/react";
import { Info } from "lucide-react";

import FilterForm from "../../components/FilterForm";
import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Loader from "../../components/loader";
import {
  getReportDescription,
  getReportRangeString,
} from "../../store/helpers";
import TicketsPerAgentReport from "../../components/branches/reports/TicketsPerAgent";
import { REPORT_RANGES, REPORT_TYPE_IDS } from "../../store/constants";
import _ from "lodash";
import { Helmet } from "react-helmet";
import DownloadReport from "../../components/DownloadReport";
import SelectBranchInfo from "../../components/misc/SelectBranchInfo";
import ReportPresentationControls from "../../components/reports/ReportPresentationControls";
import ReportPresentationSummary from "../../components/reports/ReportPresentationSummary";
import useReportRenderSettingLoader from "../../hooks/useReportRenderSettingLoader";
import {
  AGENT_TICKETS_PER_SERVICE_COLUMN_DEFINITIONS,
  DEFAULT_RENDER_CONFIG,
  getRenderConfigSummaryItems,
  normalizeRenderConfig,
} from "../../store/renderConfig";
import { saveReportRenderSetting } from "../../store/actions/defaultAction";
import { downloadTicketsPerAgentCsvFiles } from "../../store/ticketsPerAgentExport";

const TicketsPerAgent = ({
  showFilterForm = true,

  showTitle = 1,
}) => {
  const dispatch = useDispatch();
  const { userData } = useSelector((state) => state.authReducer);
  const { loading, reportRange, reportsData, branchId, organizationId, savingReportRenderSetting } = useSelector(
    (state) => state.defaultReducer
  );
  const [renderConfig, setRenderConfig] = useState(DEFAULT_RENDER_CONFIG);

  const reportTypeId =
    reportRange === REPORT_RANGES.WEEK
      ? REPORT_TYPE_IDS.weekly_branch_tickets_per_agent
      : REPORT_TYPE_IDS.monthly_branch_tickets_per_agent;

  const data = useMemo(() => {
    let reports = reportsData.filter((a) => a.reportTypeId === reportTypeId);
    if (branchId.length > 0) {
      reports = reports.filter((a) => a.branchId === branchId);
    }
    return reports.map((a) => a.data) || [];
  }, [branchId, reportTypeId, reportsData]);

  const title = "Tickets Per Agent";
  const renderConfigOptions = useMemo(
    () => ({ columnDefinitions: AGENT_TICKETS_PER_SERVICE_COLUMN_DEFINITIONS }),
    []
  );
  const isLoadingRenderSetting = useReportRenderSettingLoader({
    userId: userData?.id,
    organizationId,
    reportTypeId,
    renderConfigOptions,
    setRenderConfig,
  });
  const effectiveRenderConfig = useMemo(
    () => normalizeRenderConfig(renderConfig, renderConfigOptions),
    [renderConfig, renderConfigOptions]
  );
  const presentationSummaryItems = useMemo(
    () => getRenderConfigSummaryItems(effectiveRenderConfig, renderConfigOptions),
    [effectiveRenderConfig, renderConfigOptions]
  );

  const saveRenderConfig = () => {
    dispatch(
      saveReportRenderSetting({
        userId: userData.id,
        organizationId,
        reportTypeId,
        renderConfig: effectiveRenderConfig,
      })
    );
  };

  return (
    <Box>
      {showTitle === 1 && (
        <Helmet>
          <title>
            {getReportRangeString(reportRange)} Branch Tickets Per Agent
          </title>
        </Helmet>
      )}
      {showFilterForm ? <FilterForm /> : <></>}
      {loading || isLoadingRenderSetting ? (
        <>
          <Loader />
        </>
      ) : (
        <>
          {" "}
          {!_.isEmpty(data) ? (
            <>
              <HStack flexDir={"row"} justifyContent={"space-between"}>
                <Heading fontSize="34px" color="#222" fontWeight="700">
                  {title}

                  <Tooltip.Root>
                    <Tooltip.Trigger asChild>
                      <Icon as={Info} w={5} h={5} ml={2} cursor="pointer" />
                    </Tooltip.Trigger>
                    <Tooltip.Positioner>
                      <Tooltip.Content>
                        {getReportDescription(reportRange).TicketsByAgents}
                      </Tooltip.Content>
                    </Tooltip.Positioner>
                  </Tooltip.Root>
                </Heading>

                <DownloadReport
                  reportRange={reportRange}
                  reportTypeId={reportTypeId}
                  renderConfig={effectiveRenderConfig}
                  onSave={saveRenderConfig}
                  isSaving={savingReportRenderSetting}
                  onDownloadCsv={() =>
                    downloadTicketsPerAgentCsvFiles({
                      data,
                      renderConfig: effectiveRenderConfig,
                    })
                  }
                />
              </HStack>

              <ReportPresentationControls
                renderConfig={effectiveRenderConfig}
                onChange={setRenderConfig}
                columnDefinitions={AGENT_TICKETS_PER_SERVICE_COLUMN_DEFINITIONS}
                defaultSortColumn="total"
              />
              <ReportPresentationSummary items={presentationSummaryItems} />

              <TicketsPerAgentReport
                data={data}
                renderConfig={effectiveRenderConfig}
                onRenderConfigChange={setRenderConfig}
              />
            </>
          ) : (
        <SelectBranchInfo message={'Update Filters and Hit Search to retrieve data'}/>
      )}
        </>
      )}
    </Box>
  );
};

export default TicketsPerAgent;
